import { z } from "zod";
import { LIMITS, emailSchema, inputValuesSchema } from "./common.js";
import { actionIdSchema, conversationIdSchema, messageIdSchema, runIdSchema, tenantIdSchema } from "./ids.js";

export const RUN_MODES = ["auto", "approved", "override", "test"] as const;
export type RunMode = (typeof RUN_MODES)[number];

/** Body of every webhook delivery. `inputs` holds every input with every source resolved. */
export const webhookPayloadSchema = z.strictObject({
  run: z.strictObject({
    id: runIdSchema,
    attempt: z.int().min(1).max(4),
    mode: z.enum(RUN_MODES),
  }),
  action: z.strictObject({ id: actionIdSchema, name: z.string() }),
  inputs: inputValuesSchema,
  message: z.strictObject({
    id: messageIdSchema,
    conversationId: conversationIdSchema,
    receivedAt: z.iso.datetime(),
    from: z.strictObject({ email: emailSchema, name: z.string().optional() }),
    to: emailSchema,
    subject: z.string(),
    text: z.string().max(LIMITS.messageText),
    attachments: z.array(
      z.strictObject({
        filename: z.string(),
        contentType: z.string(),
        bytes: z.int().min(0),
        url: z.url().meta({ description: "Presigned, valid 15 minutes" }),
      }),
    ),
  }),
  decision: z.strictObject({
    confidence: z.number().min(0).max(1),
    reason: z.string().max(LIMITS.reason),
  }),
  tenant: z.strictObject({ id: tenantIdSchema, name: z.string() }),
});
export type WebhookPayload = z.infer<typeof webhookPayloadSchema>;

/** The JSON body a receiver may return with a 2xx status. An empty or non-JSON 2xx body means `{ ok: true }`. */
export const webhookResponseSchema = z.object({
  ok: z.boolean(),
  error: z.string().max(LIMITS.reason).optional().meta({ description: "Shown to the tenant in the trace when ok is false" }),
  reply: z
    .object({ text: z.string().min(1).max(10_000) })
    .optional()
    .meta({ description: "Sent to the sender when the action's afterRun.replyWith is 'response'" }),
  note: z.string().max(500).optional().meta({ description: "Stored on the run, shown in the trace" }),
});
export type WebhookResponse = z.infer<typeof webhookResponseSchema>;

export const WEBHOOK_HEADERS = {
  version: "X-Hooky-Version",
  delivery: "X-Hooky-Delivery",
  run: "X-Hooky-Run",
  timestamp: "X-Hooky-Timestamp",
  signature: "X-Hooky-Signature",
} as const;
